/**
 * WHERE THE BALL IS GOING TO LAND — the sim's own ballistics, run forward.
 *
 * Owner, 2026-08-09: *"in corner show a trial for the ball where its gonna land
 * like FIFA game"*.
 *
 * ── Why this file exists instead of a constant ──────────────────────────────
 *
 * This project has already deleted one on-pitch landing marker, and the note it
 * left behind in `client/src/render/indicators.ts` is the specification for this
 * module. Verbatim:
 *
 *   > **7 m was a CONSTANT**. It never read the charge, the loft or the power,
 *   > so it did not mark where the ball would land — a full-power shot leaves
 *   > the boot and travels thirty-odd metres past it. […] A real landing-spot
 *   > predictor would need the sim's own ballistics (§3.2 drag + Magnus […])
 *
 * So: the sim's own ballistics, or nothing. `stepPredict` below is a line-for-
 * line mirror of `ball.ts integrateAero` — the same drag curve through
 * `ballDragCoefficient` (imported, not copied), the same `MAGNUS_CL_COEFF ·
 * spinParam^¼` lift clamp, the same `dpow025`, the same semi-implicit Euler at
 * the same FIXED_DT, followed by the same per-tick air spin decay. It is not a
 * simplified model that agrees "closely enough": `ball-predict.test.ts` steps a
 * real WorldState and this predictor side by side and requires the flight paths
 * to agree to a millimetre.
 *
 * ⚠ Do NOT reach for the two prediction helpers that already existed:
 *   - `goalkeeper.ts predictThreat` deliberately omits Magnus (its own header
 *     says so) and is not exported;
 *   - `ai/ball-travel.ts predictBall` holds horizontal velocity constant in the
 *     air — no drag, no Magnus — and its header calls that inaccuracy
 *     intentional (§10.2). It is metres out over a corner's flight, which is
 *     exactly the lie the deleted marker told.
 *
 * ── What this predicts, and what it cannot ──────────────────────────────────
 *
 * The LAUNCH is fully deterministic in production: `CURVE_ENABLED` and
 * `STRIKE_AIM_ERROR_ENABLED` are both `false` (constants.ts, the KICK
 * DIRECTIVE), so `buildPass`'s two Gaussian draws are multiplied by zero and the
 * struck velocity is a pure function of the input command. Given the launch
 * state, this returns where the ball lands, and it is not a guess.
 *
 * What it does NOT know is the launch state before the taker has decided on one.
 * The AI picks its target and charge at 10 Hz, and it re-solves the lead at
 * release against the receivers' live velocities. The caller therefore has to be
 * honest about which question it is asking, and `render/cornerTrail.ts` is: it
 * draws the AIM path, labelled as the aim.
 *
 * Deterministic and allocation-free: the caller owns the output buffer, no RNG
 * is drawn, and nothing here reads or writes WorldState (§15.2, §15.3, §15.7).
 */

import {
  AIR_DENSITY_SEA_LEVEL,
  BALL_AREA,
  BALL_GROUND_REST_SPEED,
  BALL_MASS,
  BALL_RADIUS,
  BALL_RESTITUTION_GRASS,
  BALL_SPIN_DECAY_AIR,
  FIXED_DT,
  GRAVITY,
  MAGNUS_CL_COEFF,
  MAGNUS_CL_MAX,
  WET_RESTITUTION_MULTIPLIER,
} from './constants.js';
import { clamp, dpow025 } from '../math/vec3.js';
import { ballDragCoefficient } from './ball.js';

/** Launch state: where the ball is and how it is moving and spinning. */
export interface PredictLaunch {
  px: number; py: number; pz: number;
  vx: number; vy: number; vz: number;
  /** Angular velocity, rad/s. Zero is legal and means "no Magnus". */
  wx: number; wy: number; wz: number;
}

/** Air the ball flies through. Mirrors the fields `MatchConfig` supplies. */
export interface PredictAir {
  airDensity: number;
  wetPitch: boolean;
}

/** Sea level, dry — the production default, for a caller with no config to hand. */
export const DEFAULT_PREDICT_AIR: PredictAir = {
  airDensity: AIR_DENSITY_SEA_LEVEL,
  wetPitch: false,
};

/**
 * The flight, sampled.
 *
 * `points` is a flat [x,y,z, x,y,z, …] run of `count` samples — flat because the
 * only consumer is a three.js BufferAttribute, and a Vector3[] would allocate
 * `count` objects every frame the aim moves.
 */
export interface PredictedFlight {
  points: Float32Array;
  count: number;
  /** Where it first touches the turf. Valid only when `landed` is true. */
  landX: number;
  landZ: number;
  /** Seconds from launch to that first touch. */
  landTimeS: number;
  /** False when the horizon ran out first — the caller must not draw a landing. */
  landed: boolean;
}

/** Allocate a reusable result for `maxSamples` points. Call once, at init. */
export function createPredictedFlight(maxSamples: number): PredictedFlight {
  return {
    points: new Float32Array(maxSamples * 3),
    count: 0,
    landX: 0,
    landZ: 0,
    landTimeS: 0,
    landed: false,
  };
}

/** Mutable integration cursor, so the hot loop allocates nothing. */
const cur = {
  px: 0, py: 0, pz: 0,
  vx: 0, vy: 0, vz: 0,
  wx: 0, wy: 0, wz: 0,
};

/**
 * One tick of flight, identical to `ball.ts integrateAero` + the air spin decay
 * that follows it in `stepBall`.
 *
 * The spin decay is not optional garnish. Omitting it leaves ω at its launch
 * magnitude for the whole flight, and over the ~1.5 s of a corner that
 * over-integrates Magnus enough to move the landing point by a visible margin —
 * i.e. it reintroduces exactly the class of error this module exists to remove.
 */
function stepPredict(rho: number): void {
  const speedSq = cur.vx * cur.vx + cur.vy * cur.vy + cur.vz * cur.vz;
  const speed = Math.sqrt(speedSq);

  let ax = 0;
  let ay = GRAVITY;
  let az = 0;

  if (speed > 1e-6) {
    const cd = ballDragCoefficient(speed);
    const dragK = (0.5 * rho * cd * BALL_AREA * speed) / BALL_MASS;
    ax += -dragK * cur.vx;
    ay += -dragK * cur.vy;
    az += -dragK * cur.vz;

    const wSq = cur.wx * cur.wx + cur.wy * cur.wy + cur.wz * cur.wz;
    if (wSq > 1e-9) {
      const wMag = Math.sqrt(wSq);
      const spinParam = (BALL_RADIUS * wMag) / speed;
      const cl = clamp(MAGNUS_CL_COEFF * dpow025(spinParam), 0, MAGNUS_CL_MAX);
      if (cl > 0) {
        const s = (0.5 * rho * BALL_AREA * BALL_RADIUS * cl) / BALL_MASS;
        const cx = cur.wy * cur.vz - cur.wz * cur.vy;
        const cy = cur.wz * cur.vx - cur.wx * cur.vz;
        const cz = cur.wx * cur.vy - cur.wy * cur.vx;
        ax += s * cx;
        ay += s * cy;
        az += s * cz;
      }
    }
  }

  cur.vx += ax * FIXED_DT;
  cur.vy += ay * FIXED_DT;
  cur.vz += az * FIXED_DT;

  cur.px += cur.vx * FIXED_DT;
  cur.py += cur.vy * FIXED_DT;
  cur.pz += cur.vz * FIXED_DT;

  const airDecay = 1 - BALL_SPIN_DECAY_AIR * FIXED_DT;
  cur.wx *= airDecay;
  cur.wy *= airDecay;
  cur.wz *= airDecay;
}

/**
 * Integrate `launch` forward until it lands or `horizonS` runs out.
 *
 * Writes into `out` and returns it. `sampleEveryTicks` thins the polyline (the
 * integration itself is always 60 Hz — thinning the STEP would be a different,
 * wrong trajectory); the landing point is always recorded exactly, off the tick
 * it actually happens, not off a thinned sample.
 */
export function predictFlight(
  launch: PredictLaunch,
  air: PredictAir,
  horizonS: number,
  sampleEveryTicks: number,
  out: PredictedFlight,
): PredictedFlight {
  const maxSamples = out.points.length / 3;
  const rho = air.airDensity;

  cur.px = launch.px; cur.py = launch.py; cur.pz = launch.pz;
  cur.vx = launch.vx; cur.vy = launch.vy; cur.vz = launch.vz;
  cur.wx = launch.wx; cur.wy = launch.wy; cur.wz = launch.wz;

  out.count = 0;
  out.landed = false;
  out.landX = cur.px;
  out.landZ = cur.pz;
  out.landTimeS = 0;

  // Sample 0 is the launch point, so the drawn trail starts at the ball.
  out.points[0] = cur.px;
  out.points[1] = cur.py;
  out.points[2] = cur.pz;
  out.count = 1;

  const horizonTicks = Math.max(1, Math.round(horizonS / FIXED_DT));
  const every = Math.max(1, sampleEveryTicks | 0);

  for (let t = 1; t <= horizonTicks; t++) {
    const prevY = cur.py;
    const prevX = cur.px;
    const prevZ = cur.pz;

    stepPredict(rho);

    if (cur.py <= BALL_RADIUS) {
      // Crossed the turf inside this tick. Interpolate the crossing rather than
      // reporting the tick's end position: at a corner's descent speed a whole
      // tick is ~25 cm of travel, and a marker a quarter of a metre from the
      // real first touch is a marker that is wrong for no reason.
      const dy = prevY - cur.py;
      const f = dy > 1e-9 ? clamp((prevY - BALL_RADIUS) / dy, 0, 1) : 0;
      const hitX = prevX + (cur.px - prevX) * f;
      const hitZ = prevZ + (cur.pz - prevZ) * f;
      out.landX = hitX;
      out.landZ = hitZ;
      out.landTimeS = (t - 1 + f) * FIXED_DT;
      out.landed = true;
      if (out.count < maxSamples) {
        const o = out.count * 3;
        out.points[o] = hitX;
        out.points[o + 1] = BALL_RADIUS;
        out.points[o + 2] = hitZ;
        out.count++;
      }
      return out;
    }

    if (t % every === 0 && out.count < maxSamples) {
      const o = out.count * 3;
      out.points[o] = cur.px;
      out.points[o + 1] = cur.py;
      out.points[o + 2] = cur.pz;
      out.count++;
    }
  }

  // Horizon exhausted with the ball still airborne. `landed` stays false and the
  // caller draws no landing ring — an honest "I do not know yet" beats a ring
  // drawn under a ball that is still climbing.
  return out;
}

/**
 * First-bounce restitution, for a caller that wants to show the ball checking up
 * off the turf rather than stopping dead at the landing ring.
 *
 * Matches `resolveSurface`'s ground branch: normal component reflected by
 * BALL_RESTITUTION_GRASS (× WET_RESTITUTION_MULTIPLIER on a wet pitch), and
 * settled once the rebound is under BALL_GROUND_REST_SPEED. The tangential
 * friction impulse is deliberately NOT modelled here — it couples into spin and
 * only matters after the ball is rolling, which is past anything a delivery
 * marker is claiming to describe.
 */
export function bounceVy(vy: number, wetPitch: boolean): number {
  const e = BALL_RESTITUTION_GRASS * (wetPitch ? WET_RESTITUTION_MULTIPLIER : 1);
  const up = -vy * e;
  return up < BALL_GROUND_REST_SPEED ? 0 : up;
}
